import * as THREE from 'three';
import Camera from './components/Camera';
import EventDispatcher from './common/EventDispatcher';
import AssetLoader from './components/AssetLoader';
import GameScene from './components/GameScene';
import { Global } from './common/global';
import GameControl from './components/GameControl';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import RAPIER from '@dimforge/rapier3d-compat'; // Migrated from CANNON
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { Server } from './common/callServer';
import Confetti from './components/Confetti';

import './styles/main.scss';
import './styles/Home.scss';
import './styles/Instruction.scss';
import './styles/Game.scss';
import './styles/Register.scss';

import { isMobile, isMobileOnly } from 'mobile-device-detect';

import Sounds from './components/Sounds';
import Shooter from './components/Shooter';
import Ball from './components/Ball';
import Keeper from './components/Keeper';
import UI from './components/UI/UI';
import gsap from 'gsap';
import Lights from './components/Lights';
import SwipeInput from './components/SwipeInput';

let lastTime = 0;
let fps = 0;
let frameCount = 0;
let lastDeltaVec = new THREE.Vector3();

export default class Game extends THREE.Scene {
  environmentMap = null;
  sizes = {
    width: window.innerWidth,
    height: window.innerHeight,
  };

  constructor() {
    super();
    window.addEventListener('resize', this.onResize.bind(this));

    this.scoreData = {
      bar_segment_0: { score: 1000, color: 'blue' },
      bar_segment_1: { score: 800, color: 'yellow' },
      bar_segment_2: { score: 600, color: 'blue' },
      bar_segment_3: { score: 200, color: 'yellow' },
      bar_segment_4: { score: 200, color: 'blue' },
      bar_segment_5: { score: 600, color: 'yellow' },
      bar_segment_6: { score: 800, color: 'blue' },
      bar_segment_7: { score: 1000, color: 'yellow' },
      bar_segment_left: { score: 100, color: 'yellow' },
      bar_segment_right: { score: 100, color: 'blue' },
    };
  }

  // Changed to async to support RAPIER.init()
  async init() {
    this.canRender = false;
    lastTime = 0;
    fps = 0;
    this.ballLaunched = false;
    frameCount = 0;
    this.assetsLoaded = false;
    Global.gameTry = 1;
    Global.userRegistered = false;
    Global.controlType = null;
    this.fixedTimeStep = 1 / 60;
    this.accumulator = 0;

    this.physicsTimeScale = isMobile ? 0.95 : 1;
    this.gameTime = 0;
    this.events = EventDispatcher.getObj();
    this.events.addEventListener('LOADER_EVENTS', this.onEvents.bind(this));
    this.events.addEventListener('GAME_EVENTS', this.onEvents.bind(this));
    this.events.addEventListener('GAMESCENE_EVENTS', this.onEvents.bind(this));
    this.events.addEventListener('PLAYER_EVENTS', this.onEvents.bind(this));

    this.canvas = document.querySelector('canvas.webgl');
    this.clock = new THREE.Clock();

    // --- Rapier Initialization ---
    await RAPIER.init();
    this.initPhysics();

    this.camera = new Camera(
      60,
      this.sizes.width / this.sizes.height,
      0.1,
      200
    );
    this.camera.init();
    this.add(this.camera);

    this.createLevel();

    this.ui = new UI();
    this.ui.init();

    this.gameSounds = new Sounds(this, this.camera);
    this.gameSounds.init();

    this.gameControl = new GameControl(this);
    this.gameControl.init();

    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      precision: 'highp',
      powerPreference: 'high-performance',
    });

    this.renderer.physicallyCorrectLights = true;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMappingExposure = 1.3;
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.gammaFactor = 2.2;
    this.renderer.gammaOutput = false;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.outputEncoding = THREE.sRGBEncoding;
    this.renderer.setSize(this.sizes.width * 1, this.sizes.height * 1);
    this.renderer.setPixelRatio(
      Math.min(Math.max(2, window.devicePixelRatio), isMobile ? 1.5 : 1.5)
    );

    this.assetLoader = new AssetLoader(this);
    this.assetLoader.init();

    // this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    // this.controls.enableDamping = true;
    // this.controls.dampingFactor = 0.05;
    // this.controls.rotateSpeed = -0.8;
    // this.controls.enabled = true;
    // this.controls.minDistance = 1;
    // this.controls.maxDistance = 30;

    // this.controls.target.set(0, -3, 5);

    this.onResize();
    this.tick();

    const rgbeLoader = new RGBELoader();
    rgbeLoader.load(
      './textures/environmentMaps/1/bg2.hdr',
      (environmentMap) => {
        environmentMap.mapping = THREE.EquirectangularReflectionMapping;
        Global.envMap = environmentMap;

        this.assetLoader.prepare();
      }
    );
  }

  animateButtons() {
    this.frameDate = { value: 1 };
    this.lastVal = 0;
    const selectors = [
      '#ageYes',
      '#ageNo',
      '#ageConfirm',
      '#privacy_notice #proceedBtn',
      '#register_sec #getOTPBtn',
      '#code_sec #codeVerifyBtn',
      '#start_sec #startNowBtn',
      '#start_sec #playNow',
      '#intro_scr #letsPlay',
    ];

    gsap.to(this.frameDate, {
      value: 31,
      duration: 1.5,
      ease: 'none',
      repeat: -1,
      delay: 1,
      onUpdate: function () {
        if (this.lastVal != Math.floor(this.frameDate.value)) {
          selectors.forEach((sel) => {
            const el = document.querySelector(sel);
            if (el) el.classList.remove(`sprite_white${this.lastVal}`);
          });

          this.lastVal = Math.floor(this.frameDate.value);

          selectors.forEach((sel) => {
            const el = document.querySelector(sel);
            if (el) el.classList.add(`sprite_white${this.lastVal}`);
          });
        }
      }.bind(this),
    });
  }

  createLevel() {
    // Note: initPhysics is now called inside init() to ensure RAPIER is ready
    this.gameScene = new GameScene(this);
    this.gameScene.init();

    this.ball = new Ball(this);
    this.ball.init();

    this.lights = new Lights(this);
    this.lights.init();

    this.shooter = new Shooter(this);
    this.shooter.init();

    this.swipeInput = new SwipeInput();
    // this.swipeInput.setEnabled(true);
  }

  initPhysics() {
    // Creating Rapier World
    const gravity = { x: 0.0, y: -9.81, z: 0.0 };
    this.world = new RAPIER.World(gravity);
    Global.physicsWorld = this.world;

    // We keep the Global material references so Ball/GameScene don't break,
    // but in Rapier, friction/restitution are set directly on the Colliders.
    Global.ballMaterial = { friction: 1, restitution: 0.25 };
    Global.floorMaterial = { friction: 1, restitution: 0.25 };
    Global.netMaterial = { friction: 0.04, restitution: 0.2 };
    Global.netMaterial2 = { friction: 0.04, restitution: 0.2 };
  }

  showStartCounter() {
    this.countDown = new Countdown(this, this.camera);
    this.countDown.init();
  }

  startGame() {
    Global.isGameActive = true;
    if (Global.gameTry == 1) {
      this.events.dispatchEvent({
        type: 'SOUND_EVENTS',
        message: { event_type: 'play_sound', data: { sndKey: 'blow' } },
      });
      this.introRotateTwn = gsap.to('.kick_icon', {
        rotation: 360, // Rotates to 360 degrees
        duration: 5,
        repeat: -1,
        ease: 'none', // Keeps the speed constant
        delay: 0,
      });
      document.querySelector('.kick_icon').classList.add('active');
      document.querySelector('.kick_intro').classList.add('active');
      gsap.delayedCall(0.1, () => {
        document.querySelector('.kick_icon').classList.add('active2');
        document.querySelector('.kick_intro').classList.add('active2');
      });
    }
  }

  hideAllScreen() {}

  onEvents(data) {
    switch (data['message']['event_type']) {
      case 'load_complete':
        if (this.assetsLoaded) return;
        this.assetsLoaded = true;
        document.querySelector('#splash_scr').classList.remove('active');
        this.setUpGame();
        break;
      case 'replay_game':
        this.replayGame(false);

        break;
      case 'init_scene':
        this.showScene();
        this.setupFreekick();
        break;
      case 'start_game':
        this.startGameTimer();

        this.startGame();
        break;
      case 'game_over':
        this.stopGame(true);
        break;
      case 'defend':
      case 'retry':
        this.prepareNextTry(data['message']['event_type']);
        break;
      case 'launch_ball':
        this.onLaunch();
        break;
      case 'kick_ball':
        this.onKick();
        break;
      case 'ball_crossed_distance':
        this.onBallCrossedPost();
        break;
    }
  }
  onKick() {
    if (this.introRotateTwn) {
      this.introRotateTwn.kill();
      this.introRotateTwn = null;
      document.querySelector('.kick_icon').classList.remove('active');
      document.querySelector('.kick_intro').classList.remove('active');
      document.querySelector('.kick_icon').classList.remove('active2');
      document.querySelector('.kick_intro').classList.remove('active2');
    }
  }
  onBallCrossedPost() {
    if (!this.ballLaunched) return;

    this.ballLaunched = false;
    gsap.to(`#miss_icon`, {
      scale: 1, // Rotates to 360 degrees
      duration: 0.4,
      ease: 'back.out', // Keeps the speed constant
      delay: 0,
    });
    gsap.to(`#miss_icon`, {
      scale: 0, // Rotates to 360 degrees
      duration: 0.4,
      ease: 'back.in', // Keeps the speed constant
      delay: 1,
    });
    this.events.dispatchEvent({
      type: 'SOUND_EVENTS',
      message: { event_type: 'play_sound', data: { sndKey: 'miss' } },
    });
  }
  onLaunch() {
    this.ballLaunched = true;
  }

  showScene() {
    this.canRender = true;
    // this.events.dispatchEvent({
    //   type: 'SOUND_EVENTS',
    //   message: { event_type: 'stop_sound', data: { sndKey: 'music' } },
    // });
    // gsap.delayedCall(0.1, () => {

    // });
    gsap.delayedCall(0.25, () => {
      document.querySelector('#game_view').classList.add('active');
    });
  }

  async onGameFinish() {
    Global.isGameActive = false;
    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'show_screen',
        data: {
          screenKey: 'Score',
        },
      },
    });
  }

  startGameTimer() {
    if (Global.introShown) {
      this.events.dispatchEvent({
        type: 'GAMESCENE_EVENTS',
        message: { event_type: 'show_screen', data: { screenKey: 'Game' } },
      });
      this.events.dispatchEvent({
        type: 'GAMESCENE_EVENTS',
        message: { event_type: 'start_time', data: {} },
      });
    }
    Global.kicksTriggerd = 0;
    Global.scoreTotal = 0;
    document.querySelector('.score_sec').classList.add('active');
    document.querySelector('.score_sec .try .tryLabel').innerHTML =
      `${Global.kicksTriggerd}/${Global.totalKicks}`;
    document.querySelector('.score_sec .score').innerHTML =
      `SCORE ${Global.scoreTotal}`;
  }

  stopTimer() {
    this.gameTimer && clearInterval(this.gameTimer);
  }

  updateTime() {
    this.gameTime += 100;
    let totalSeconds = Math.floor(this.gameTime / 1000);
    let minutes = Math.floor(totalSeconds / 60);
    let seconds = totalSeconds % 60;

    const timeString = `${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    const el = document.querySelector('#info_time .txt');
    if (el) el.innerHTML = timeString;
  }

  setUpGame() {
    this.environment = Global.envMap;
    this.background = Global.envMap;
    this.backgroundIntensity = 1;
    // Global.envMap.rotation = Math.PI / 2;

    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: { event_type: 'create_scene', data: {} },
    });
    this.events.dispatchEvent({
      type: 'SOUND_EVENTS',
      message: { event_type: 'init_sounds', data: {} },
    });
    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: { event_type: 'init_scene', data: {} },
    });
    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'show_screen',
        data: {
          screenKey: 'Home',
        },
      },
    });
    // this.onGameFinish();
  }

  stopGame(quickTransition) {
    Global.isGameActive = false;
    this.canRender = false;
    this.hideHeader();
    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: { event_type: 'hide_intro', data: {} },
    });

    this.events.dispatchEvent({
      type: 'GAME_EVENTS',
      message: { event_type: 'dispose', data: {} },
    });
    this.dispose();

    this.events.dispatchEvent({
      type: 'SOUND_EVENTS',
      message: { event_type: 'stop_sound', data: { sndKey: 'crowd' } },
    });
    gsap.delayedCall(0.1, () => {
      this.events.dispatchEvent({
        type: 'SOUND_EVENTS',
        message: { event_type: 'play_sound', data: { sndKey: 'music' } },
      });
    });

    Global.serverObj.send(Global.URL_DATA, null, null, {
      saveType: 'score',
      score: Global.totalGoalsSaved,
      yob: Global.yob,
      gender: Global.gender,
      name: Global.userName,
      mobile: Global.mobile,
      email: Global.email,
      jerseyname: Global.avatarName,
      location: Global.location,
      IGhandle: Global.IGHandle,
      uniqID: Global.U_ID,
    });

    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: { event_type: 'show_screen', data: { screenKey: 'Score' } },
    });
  }

  prepareNextTry(type) {
    gsap.delayedCall(type == 'retry' ? 0 : 4, this.setupFreekick.bind(this));
  }

  showHeader() {
    return;
    const el = document.querySelector('#game_header');
    if (el) {
      el.classList.add('active');
      gsap.delayedCall(0.1, () => el.classList.add('active2'));
    }
  }

  hideHeader() {
    return;
    const el = document.querySelector('#game_header');
    if (el) {
      el.classList.remove('active');
      el.classList.remove('active2');
    }
  }

  setupFreekick() {
    if (Global.kicksTriggerd >= Global.totalKicks) {
      this.onGameFinish();
      return;
    }
    if (Global.shootCnt == 0) {
      this.events.dispatchEvent({
        type: 'GAMESCENE_EVENTS',
        message: { event_type: 'show_screen', data: { screenKey: 'Game' } },
      });
    }

    Global.shootDirection = null;
    Global.shootCnt++;
    Global.xPadd = 0;
    const ballPosition = new THREE.Vector3(0, 0, 8);

    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'assign_position',
        data: { position: ballPosition },
      },
    });
    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'reset_camera',
        data: { position: ballPosition },
      },
    });
    this.swipeInput.setEnabled(true);

    if (!Global.isGameActive) return;

    gsap.delayedCall(0.2, () => {
      this.events.dispatchEvent({
        type: 'SOUND_EVENTS',
        message: { event_type: 'play_sound', data: { sndKey: 'blow' } },
      });
    });

    this.showHeader();
    // gsap.delayedCall(1, () => {
    //   this.events.dispatchEvent({
    //     type: 'GAMESCENE_EVENTS',
    //     message: {
    //       event_type: 'kick_ball',
    //       data: {
    //         curve_fact: this.swipeX - this.initSwipeX,
    //         swipeDir: Math.sign(this.swipeX),
    //       },
    //     },
    //   });
    // });

    if (Global.shootCnt == 1 && !Global.introShown) {
      this.events.dispatchEvent({
        type: 'GAMESCENE_EVENTS',
        message: { event_type: 'show_screen', data: { screenKey: 'Intro' } },
      });
    }
  }

  replayGame(isGameTry = false) {
    Global.kicksTriggerd = 0;
    Global.gameTry++;
    Global.shootDirection = null;
    Global.shootCnt = 0;
    // this.createLevel();

    // this.events.dispatchEvent({
    //   type: 'GAMESCENE_EVENTS',
    //   message: { event_type: 'create_scene', data: {} },
    // });
    // this.events.dispatchEvent({
    //   type: 'GAMESCENE_EVENTS',
    //   message: { event_type: 'init_scene', data: {} },
    // });
    // this.events.dispatchEvent({
    //   type: 'GAMESCENE_EVENTS',
    //   message: { event_type: 'update_post_feature', data: {} },
    // });

    gsap.delayedCall(0.3, () => {
      this.events.dispatchEvent({
        type: 'GAMESCENE_EVENTS',
        message: { event_type: 'start_game', data: {} },
      });
      this.setupFreekick();
    });
  }

  onResize() {
    this.sizes.width = window.innerWidth;
    this.sizes.height = window.innerHeight;

    this.events.dispatchEvent({
      type: 'CAMERA_EVENTS',
      message: {
        event_type: 'resize',
        data: {
          aspect: this.sizes.width / this.sizes.height,
          sizes: this.sizes,
        },
      },
    });

    this.renderer.setSize(this.sizes.width, this.sizes.height);
    this.renderer.setPixelRatio(
      Math.min(Math.max(2, window.devicePixelRatio), isMobile ? 1.5 : 1.5)
    );
  }

  update(now, delta) {
    this.renderer.render(this, this.camera);
    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'update',
        data: { delta: delta, camera: this.camera, now: now },
      },
    });
  }

  tick(now) {
    if (!this.canRender) {
      window.requestAnimationFrame(this.tick.bind(this));
      return;
    }
    this.stats && this.stats.begin();
    this.controls && this.controls.update();

    const delta = (now - lastTime) / 1000;
    lastTime = now;

    frameCount++;
    if (delta) {
      fps = frameCount / delta;
      frameCount = 0;
    }

    let ticks = Math.ceil(delta / (1 / 120));
    ticks = Math.min(ticks, 20);
    Global.fps = new THREE.Vector3(0, Global.fps, 0).lerp(
      new THREE.Vector3(0, fps, 0),
      0.1
    ).y;

    const newDelta = Math.min(
      (delta ? Math.min(delta * 100, 1) : 1) * (120 / (fps || 1)),
      10
    );
    Global.delta = THREE.MathUtils.lerp(Global.delta, newDelta, 0.01);
    const scaledDelta = delta * this.physicsTimeScale;

    this.update(now, lastDeltaVec.y);

    if (!isNaN(scaledDelta) && this.world) {
      this.accumulator += scaledDelta;
      while (this.accumulator >= this.fixedTimeStep) {
        this.ball && this.ball.updateTrail();
        // Rapier World Step
        const eventQueue = new RAPIER.EventQueue(true);
        this.world.step(eventQueue);

        eventQueue.drainCollisionEvents((handle1, handle2, started) => {
          if (started && this.ballLaunched) {
            this.processCollision(handle1, handle2);
          }
        });

        this.accumulator -= this.fixedTimeStep;
      }
    }

    if (this.confetti) {
      const wind = new THREE.Vector3(
        Math.sin(now * 0.9) * 1,
        0,
        Math.cos(now * 0.6) * 0
      );
      this.confetti.update(delta, wind);
    }

    this.stats && this.stats.end();
    window.requestAnimationFrame(this.tick.bind(this));
  }
  processCollision(handle1, handle2) {
    this.ballLaunched = false;
    const body1 = Global.physicsWorld.getRigidBody(handle1);
    const body2 = Global.physicsWorld.getRigidBody(handle2);

    console.log(body1.userData.name, 'body1.userData.name');
    if (body1.userData.name.includes('bar_segment')) {
      const _score = this.scoreData[body1.userData.name].score;
      const _color = this.scoreData[body1.userData.name].color;
      Global.scoreTotal += this.scoreData[body1.userData.name].score;
      gsap.to(`#_${_score}_${_color}`, {
        scale: 1, // Rotates to 360 degrees
        duration: 0.4,
        ease: 'back.out', // Keeps the speed constant
        delay: 0,
      });
      gsap.to(`#_${_score}_${_color}`, {
        scale: 0, // Rotates to 360 degrees
        duration: 0.4,
        ease: 'back.in', // Keeps the speed constant
        delay: 1,
      });
      this.events.dispatchEvent({
        type: 'CAMERA_EVENTS',
        message: {
          event_type: 'shake_camera',
          data: {
            shake_fact: 0.2,
            shake_time: 0.2,
          },
        },
      });
      document.querySelector('.score_sec .score').innerHTML =
        `SCORE ${Global.scoreTotal}`;
      this.events.dispatchEvent({
        type: 'SOUND_EVENTS',
        message: { event_type: 'play_sound', data: { sndKey: 'barHit' } },
      });
      gsap.delayedCall(0.1, () => {
        this.events.dispatchEvent({
          type: 'SOUND_EVENTS',
          message: { event_type: 'play_sound', data: { sndKey: 'cheer' } },
        });
        this.events.dispatchEvent({
          type: 'SOUND_EVENTS',
          message: { event_type: 'play_sound', data: { sndKey: 'score' } },
        });
      });
    } else {
      gsap.to(`#miss_icon`, {
        scale: 1, // Rotates to 360 degrees
        duration: 0.4,
        ease: 'back.out', // Keeps the speed constant
        delay: 0,
      });
      gsap.to(`#miss_icon`, {
        scale: 0, // Rotates to 360 degrees
        duration: 0.4,
        ease: 'back.in', // Keeps the speed constant
        delay: 1,
      });
      if (body1.userData.name.includes('net')) {
        this.events.dispatchEvent({
          type: 'SOUND_EVENTS',
          message: { event_type: 'play_sound', data: { sndKey: 'net' } },
        });
      }
      this.events.dispatchEvent({
        type: 'SOUND_EVENTS',
        message: { event_type: 'play_sound', data: { sndKey: 'miss' } },
      });
    }
    return;
    // Check if either body is our ball
    const ball =
      body1 === this.ball.ballBody
        ? body1
        : body2 === this.ball.ballBody
          ? body2
          : null;
    const other = ball === body1 ? body2 : body1;

    if (ball && other) {
      // This is where you call your specific handler!
      this.ball.handleCollision(other);
    }
  }
  dispose() {
    document.querySelector('#game_view').classList.remove('active');
    document.querySelector('#game_view').classList.remove('active2');

    if (this.world) {
      // Rapier specific disposal
      this.world.free();
      this.world = null;
    }

    Global.ballMaterial = null;
    Global.floorMaterial = null;
    Global.netMaterial = null;
    Global.netMaterial2 = null;
  }
}

window.onload = async function () {
  checkDevice();

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const source = urlParams.get('utm_source');
  const medium = urlParams.get('utm_medium');
  const content = urlParams.get('utm_content');
  const term = urlParams.get('utm_term');

  Global.serverObj = new Server();
  Global.serverObj.send(
    Global.URL_CREATE,
    setUID.bind(this),
    null,
    {
      device: isMobile ? 'mobile' : 'web',
      fresh: true,
      source,
      medium,
      content,
      term,
    },
    'POST',
    null,
    false
  );

  const game = new Game();
  game.init();
};

function setUID(v) {
  const data = JSON.parse(v);
  Global.U_ID = data['UID'];
  Global.gameKey = data['gamekey'];
  Global.serverObj.setDetails(Global.U_ID, Global.gameKey);
}

function checkDevice() {
  if (!isMobile) {
    // const header = document.querySelector('#game_header');
    // if (header) header.classList.add('desktop');
  }
}
